import PropTypes from 'prop-types';
import React, { Fragment, memo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, List } from 'immutable';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { Collapse } from '../../helpers/Collapse';
import { ExpandableText } from '../../helpers/ExpandableText';
import { IconButton } from '../../helpers/IconButton';
import { Spinner } from '../../helpers/Spinner';
import { getIn } from '../../../utils/ChangeSpy';
import { DATE_FORMAT } from '../../../utils/dateFormat';

import style from './style.module.css';

const EMPTY_VALUE = '—';

const MetaItem = ({ label, isClamped = false, isFluid = false, children }) => (
  <div className={classNames(style.metaItem, { [style.fluid]: isFluid })}>
    <span className={style.label}>{label}</span>
    <span className={classNames(style.value, { [style.clamped]: isClamped })}>{children || EMPTY_VALUE}</span>
  </div>
);

MetaItem.propTypes = {
  children: PropTypes.node,
  isClamped: PropTypes.bool,
  isFluid: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

const TemplateLink = memo(({ id, name, onDownload }) => {
  const handleClick = useCallback(
    event => {
      onDownload(event, { id });
    },
    [id, onDownload],
  );

  return (
    <button className={style.linkButton} onClick={handleClick} title="Download" type="button">
      {name}
      <i aria-hidden="true" className={classNames('fa fa-download', style.externalIcon)} />
    </button>
  );
});

TemplateLink.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
};

/**
 * Read-only project details widget with collapsible scope and status.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project information data.
 * @param props.industryNames {String} Comma-separated industry names.
 * @param props.userNames {String} Comma-separated user names.
 * @param props.templates {Immutable.List} Template list; the Templates field is hidden when omitted.
 * @param props.isLoading {Boolean} Show the loading spinner.
 * @param props.isBuyerHidden {Boolean} Hide the Buyer field.
 * @param props.projectLink {String} Turns the title into an external link to the project page.
 * @param props.onEdit {Function} Handle event of opening the edit popup; the edit button is hidden when omitted.
 * @param props.onDownload {Function} Handle event of downloading template file.
 */
const ProjectDetailsComponent = ({
  projectData,
  industryNames,
  userNames,
  templates,
  isLoading = false,
  isBuyerHidden = false,
  projectLink,
  onEdit,
  onDownload,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  const scope = projectData.getIn(['details', 'name']);
  const status = projectData.getIn(['status', 'name']);
  const createdDate = dayjs(projectData.get('dateCreated'));
  const isActive = projectData.getIn(['active', 'value']);
  const type = projectData.get('type');
  const buyerName = projectData.get('buyerName');
  const companyType = projectData.get('companyType');
  const executive = projectData.getIn(['exec', 'name']);
  const executiveId = projectData.getIn(['exec', 'id']);
  const title = projectData.getIn(['category', 'name']) || 'Project';

  const templateLinks =
    templates &&
    templates.map((template, index) => (
      <Fragment key={template.get('id')}>
        {index > 0 && ', '}
        <TemplateLink id={template.get('id')} name={getIn(template, 'name')} onDownload={onDownload} />
      </Fragment>
    ));

  return (
    <section className={style.host}>
      <div className={style.header}>
        <h2 className={style.title}>
          {projectLink ? (
            <a
              className={style.titleLink}
              href={projectLink}
              rel="noopener noreferrer"
              target="_blank"
              title="Launch full project view in a new window"
            >
              {title} <i aria-hidden="true" className={classNames('fa fa-external-link', style.externalIcon)} />
            </a>
          ) : (
            title
          )}
        </h2>
        <span className={classNames('label', isActive ? 'label-success' : 'label-default')}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
        {type && <span className="label label-muted">{type}</span>}
        {onEdit && (
          <div className={style.actions}>
            <IconButton icon="pencil" onClick={onEdit} title="Edit details" />
          </div>
        )}
      </div>
      <div className={style.meta}>
        {!isBuyerHidden && (
          <MetaItem label="Buyer">
            {buyerName && (
              <>
                <Link target="_blank" to={`/company/${projectData.get('buyerId')}/buyer`}>
                  {buyerName}
                </Link>
                {companyType && <span className={style.note}> · {companyType}</span>}
              </>
            )}
          </MetaItem>
        )}
        <MetaItem label="HarvCo Lead">{projectData.getIn(['harvcoLead', 'name'])}</MetaItem>
        <MetaItem label="Client Lead">{projectData.getIn(['clientLead', 'name'])}</MetaItem>
        <MetaItem label="Executive">
          {executive && (
            <a href={`/contact/${executiveId}`} rel="noopener noreferrer" target="_blank">
              {executive} <i aria-hidden="true" className={classNames('fa fa-external-link', style.externalIcon)} />
            </a>
          )}
        </MetaItem>
        <MetaItem label="Created">{createdDate.isValid() ? createdDate.format(DATE_FORMAT) : null}</MetaItem>
        <MetaItem isClamped={!isExpanded} label="Industries" isFluid>
          {industryNames}
        </MetaItem>
        <MetaItem isClamped={!isExpanded} label="Users" isFluid>
          {userNames}
        </MetaItem>
        {templates && (
          <MetaItem isClamped={!isExpanded} label="Templates" isFluid>
            {templates.size > 0 ? templateLinks : null}
          </MetaItem>
        )}
      </div>
      <Collapse isOpen={isExpanded}>
        <div className={style.texts}>
          <MetaItem label="Project Scope">{scope && <ExpandableText>{scope}</ExpandableText>}</MetaItem>
          <MetaItem label="Status">{status && <ExpandableText>{status}</ExpandableText>}</MetaItem>
        </div>
      </Collapse>
      <div className={style.toggle}>
        <IconButton
          aria-expanded={isExpanded}
          className={classNames(style.chevron, { [style.chevronUp]: isExpanded })}
          icon="chevron-down"
          onClick={handleToggle}
          size="sm"
          title={isExpanded ? 'Hide details' : 'Show all details'}
        />
      </div>
      {isLoading && <Spinner />}
    </section>
  );
};

ProjectDetailsComponent.propTypes = {
  industryNames: PropTypes.string.isRequired,
  isBuyerHidden: PropTypes.bool,
  isLoading: PropTypes.bool,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  projectData: PropTypes.instanceOf(Map).isRequired,
  projectLink: PropTypes.string,
  templates: PropTypes.instanceOf(List),
  userNames: PropTypes.string.isRequired,
};

export const ProjectDetails = memo(ProjectDetailsComponent);
